import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, API, getStoredToken } from "@/lib/api";
import { toast } from "sonner";
import {
  BarChart3, Filter, Download, FileText, ArrowLeft, Loader2, Users, Clock,
  QrCode, Copy, ExternalLink,
} from "lucide-react";

const HCWI_SLUG = "hcwi";

export default function AdminAssessments() {
  const [definition, setDefinition] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [tab, setTab] = useState("overview"); // overview | submissions | contacts

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [defRes, statsRes] = await Promise.all([
        api.get(`/admin/assessments/${HCWI_SLUG}`),
        api.get(`/admin/assessments/${HCWI_SLUG}/stats`, { params: filters }),
      ]);
      setDefinition(defRes.data);
      setStats(statsRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const setFilter = (k, v) => setFilters((prev) => {
    const next = { ...prev };
    if (v === "" || v == null) delete next[k];
    else next[k] = v;
    return next;
  });

  // Public URL resolution priority:
  //  1. Admin override saved to localStorage (lets a single admin pin the
  //     canonical domain even if they log in from an Emergent staging host).
  //  2. process.env.REACT_APP_PUBLIC_APP_URL (set in production env vars).
  //  3. window.location.origin (fallback — works in dev/preview).
  const [publicHostOverride, setPublicHostOverride] = useState(() => {
    try { return localStorage.getItem("hw_public_host_override") || ""; } catch { return ""; }
  });
  const [editingHost, setEditingHost] = useState(false);
  const [hostDraft, setHostDraft] = useState(publicHostOverride);
  const resolvedHost = (
    publicHostOverride ||
    process.env.REACT_APP_PUBLIC_APP_URL ||
    (typeof window !== "undefined" ? window.location.origin : "")
  ).replace(/\/+$/, "");
  const publicUrl = `${resolvedHost}/hcwi`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(publicUrl)}`;

  const saveHost = () => {
    const cleaned = (hostDraft || "").trim().replace(/\/+$/, "");
    if (cleaned && !/^https?:\/\//i.test(cleaned)) {
      toast.error("Host must start with http:// or https://");
      return;
    }
    try { localStorage.setItem("hw_public_host_override", cleaned); } catch { /* localStorage unavailable */ }
    setPublicHostOverride(cleaned);
    setEditingHost(false);
    toast.success(cleaned ? `Public host set to ${cleaned}` : "Reverted to default host");
  };

  const exportCsv = async () => {
    try {
      const token = getStoredToken();
      const resp = await fetch(`${API}/admin/assessments/${HCWI_SLUG}/submissions/export.csv?` +
        new URLSearchParams(filters).toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("Export failed");
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${HCWI_SLUG}-submissions.csv`;
      a.click();
    } catch (e) { toast.error(e.message || "Export failed"); }
  };
  const exportJson = async () => {
    try {
      const token = getStoredToken();
      const resp = await fetch(`${API}/admin/assessments/${HCWI_SLUG}/submissions/export.json?` +
        new URLSearchParams(filters).toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("Export failed");
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${HCWI_SLUG}-submissions.json`;
      a.click();
    } catch (e) { toast.error(e.message || "Export failed"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-assessments-page">
      <div>
        <Link to="/reports" className="hw-eyebrow inline-flex items-center gap-1 mb-2 hover:underline" data-testid="back-to-reports">
          <ArrowLeft className="w-3 h-3" /> Back to reports
        </Link>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A]">Assessments</h1>
            <p className="text-sm text-[#334155] mt-1 max-w-2xl">
              Anonymous community wellness data collected through HarborWell&apos;s public assessment engine.
              The HCWI is the first assessment; more can be added without rebuilding this dashboard.
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={exportCsv} data-testid="export-csv-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E5E5E0] text-[#1E3A8A] text-xs font-medium hover:bg-[#F9F8F6]">
              <FileText className="w-3.5 h-3.5" /> CSV
            </button>
            <button onClick={exportJson} data-testid="export-json-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E5E5E0] text-[#1E3A8A] text-xs font-medium hover:bg-[#F9F8F6]">
              <Download className="w-3.5 h-3.5" /> JSON
            </button>
          </div>
        </div>
      </div>

      <ShareCard
        publicUrl={publicUrl}
        qrUrl={qrUrl}
        publicHostOverride={publicHostOverride}
        editingHost={editingHost}
        setEditingHost={setEditingHost}
        hostDraft={hostDraft}
        setHostDraft={setHostDraft}
        saveHost={saveHost}
      />

      <nav className="flex gap-1 border-b border-[#E5E5E0]" data-testid="assessments-tabs">
        {[
          { key: "overview", label: "Overview" },
          { key: "submissions", label: "Submissions" },
          { key: "contacts", label: "Contact leads" },
        ].map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            data-testid={`tab-${t.key}`}
            className={`px-3 py-2 text-sm font-medium border-b-2 ${
              tab === t.key ? "border-[#1E3A8A] text-[#1E3A8A]" : "border-transparent text-[#334155] hover:text-[#1E3A8A]"
            }`}>
            {t.label}
          </button>
        ))}
      </nav>

      {tab === "overview" && (
        <OverviewTab
          definition={definition}
          stats={stats}
          filters={filters}
          setFilter={setFilter}
          loading={loading}
        />
      )}
      {tab === "submissions" && (
        <SubmissionsTab definition={definition} filters={filters} />
      )}
      {tab === "contacts" && <ContactsTab />}
    </div>
  );
}

function ShareCard({ publicUrl, qrUrl, publicHostOverride, editingHost, setEditingHost, hostDraft, setHostDraft, saveHost }) {
  return (
    <div className="bg-white border border-[#E5E5E0] rounded-2xl p-4 flex items-center gap-4 flex-wrap" data-testid="assessment-share-card">
      <img src={qrUrl} alt="HCWI QR" className="w-24 h-24 rounded-lg border border-[#E5E5E0]" data-testid="hcwi-qr" />
      <div className="flex-1 min-w-[200px]">
        <div className="flex items-center gap-2 mb-1">
          <div className="hw-eyebrow">Public URL · scannable & embeddable</div>
          {publicHostOverride && (
            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#E0E7FF] text-[#1E3A8A]">Custom host</span>
          )}
        </div>
        {editingHost ? (
          <div className="space-y-2" data-testid="hcwi-host-editor">
            <input
              type="url"
              value={hostDraft}
              onChange={(e) => setHostDraft(e.target.value)}
              placeholder="https://connect.harborwell.care"
              data-testid="hcwi-host-input"
              autoFocus
              className="w-full px-3 py-1.5 rounded-full border border-[#E5E5E0] bg-white text-sm font-mono text-[#0F172A] focus:outline-none focus:border-[#1E3A8A]"
            />
            <div className="flex gap-2 flex-wrap">
              <button onClick={saveHost} data-testid="hcwi-host-save"
                className="px-3 py-1 rounded-full bg-[#1E3A8A] text-white text-[11px] font-medium hover:bg-[#172554]">
                Save
              </button>
              <button onClick={() => { setHostDraft(""); saveHost(); }} data-testid="hcwi-host-reset"
                className="px-3 py-1 rounded-full bg-white border border-[#E5E5E0] text-[#1E3A8A] text-[11px] font-medium hover:bg-[#F9F8F6]">
                Reset to default
              </button>
              <button onClick={() => setEditingHost(false)}
                className="px-3 py-1 text-[11px] text-[#334155]">
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="font-mono text-sm text-[#0F172A] truncate" data-testid="hcwi-public-url">{publicUrl}</div>
        )}
        {!editingHost && (
          <div className="flex gap-2 mt-2 flex-wrap">
            <button onClick={() => { navigator.clipboard?.writeText(publicUrl); toast.success("Copied"); }}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-[#E5E5E0] text-[#1E3A8A] text-[11px] font-medium hover:bg-[#F9F8F6]"
              data-testid="hcwi-copy-url">
              <Copy className="w-3 h-3" /> Copy link
            </button>
            <a href={publicUrl} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-[#E5E5E0] text-[#1E3A8A] text-[11px] font-medium hover:bg-[#F9F8F6]"
              data-testid="hcwi-open-url">
              <ExternalLink className="w-3 h-3" /> Open survey
            </a>
            <a href={qrUrl} download="hcwi-qr.png"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-[#E5E5E0] text-[#1E3A8A] text-[11px] font-medium hover:bg-[#F9F8F6]"
              data-testid="hcwi-download-qr">
              <QrCode className="w-3 h-3" /> Download QR
            </a>
            <button onClick={() => setEditingHost(true)} data-testid="hcwi-edit-host"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-[#E5E5E0] text-[#334155] text-[11px] font-medium hover:bg-[#F9F8F6]">
              Edit host
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function OverviewTab({ definition, stats, filters, setFilter, loading }) {
  const labelByKey = useMemo(() => {
    const m = {};
    if (!definition) return m;
    for (const s of definition.sections || []) for (const q of s.questions || []) m[q.key] = q.label;
    return m;
  }, [definition]);

  if (loading || !stats || !definition) {
    return <div className="py-8 text-center text-[#1E3A8A]"><Loader2 className="w-5 h-5 animate-spin inline" /> Loading…</div>;
  }
  const avgMin = stats.average_completion_seconds ? (stats.average_completion_seconds / 60).toFixed(1) : "—";

  return (
    <div className="space-y-6" data-testid="assessment-overview">
      <FilterBar filters={filters} setFilter={setFilter} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Total submissions" value={stats.total} icon={Users} testId="kpi-total" />
        <Kpi label="Avg completion" value={`${avgMin} min`} icon={Clock} testId="kpi-avg" />
        <Kpi label="Counties represented" value={stats.county_distribution.length} icon={BarChart3} testId="kpi-counties" />
        <Kpi label="Unique ZIPs" value={stats.zip_distribution.length} icon={BarChart3} testId="kpi-zips" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <DistributionCard title="County distribution" rows={stats.county_distribution} testId="dist-county" />
        <DistributionCard title="Top ZIPs" rows={stats.zip_distribution} testId="dist-zip" />
      </div>

      <div className="space-y-4">
        {(definition.sections || []).map((section) => {
          const closedQs = (section.questions || []).filter((q) => q.type !== "long_text" && q.type !== "short_text");
          const openQs = (section.questions || []).filter((q) => q.type === "long_text" || q.type === "short_text");
          const hasOpenAnswers = openQs.some((q) => ((stats.open_responses || {})[q.key] || []).length > 0);
          return (
            <div key={section.key} className="bg-white border border-[#E5E5E0] rounded-2xl p-4 space-y-3" data-testid={`dist-section-${section.key}`}>
              <h2 className="font-display font-semibold text-[#0F172A]">{section.title}</h2>
              {closedQs.length > 0 && (
                <div className="grid md:grid-cols-2 gap-3">
                  {closedQs.map((q) => (
                    <DistributionCard
                      key={q.key}
                      title={labelByKey[q.key] || q.key}
                      rows={stats.distributions[q.key] || []}
                      testId={`dist-q-${q.key}`}
                      compact
                    />
                  ))}
                </div>
              )}
              {openQs.length > 0 && (
                <div className="grid md:grid-cols-2 gap-3">
                  {openQs.map((q) => (
                    <OpenResponsesCard
                      key={q.key}
                      title={labelByKey[q.key] || q.key}
                      rows={(stats.open_responses || {})[q.key] || []}
                      testId={`open-q-${q.key}`}
                    />
                  ))}
                </div>
              )}
              {closedQs.length === 0 && !hasOpenAnswers && (
                <div className="text-xs text-[#94A3B8]">No open responses yet for this section.</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FilterBar({ filters, setFilter }) {
  return (
    <div className="bg-white border border-[#E5E5E0] rounded-2xl p-3 flex flex-wrap items-end gap-3" data-testid="assessment-filters">
      <div className="flex items-center gap-1.5 text-xs text-[#1E3A8A] uppercase tracking-wider">
        <Filter className="w-3.5 h-3.5" /> Filters
      </div>
      <FilterInput label="From" type="date" value={filters.from_date || ""} onChange={(v) => setFilter("from_date", v)} testId="filter-from" />
      <FilterInput label="To" type="date" value={filters.to_date || ""} onChange={(v) => setFilter("to_date", v)} testId="filter-to" />
      <FilterInput label="County" value={filters.county || ""} onChange={(v) => setFilter("county", v)} testId="filter-county" />
      <FilterInput label="ZIP" value={filters.zip || ""} onChange={(v) => setFilter("zip", v)} testId="filter-zip" />
      <FilterSelect label="Age" value={filters.age || ""} onChange={(v) => setFilter("age", v)} testId="filter-age"
        options={["Under 18", "18–24", "25–34", "35–44", "45–54", "55–64", "65+"]} />
      <FilterSelect label="Gender" value={filters.gender || ""} onChange={(v) => setFilter("gender", v)} testId="filter-gender"
        options={["Female", "Male", "Non-binary", "Prefer to self-describe", "Prefer not to answer"]} />
      <FilterSelect label="Insurance" value={filters.insurance || ""} onChange={(v) => setFilter("insurance", v)} testId="filter-insurance"
        options={["Private Insurance", "Medicaid", "Medicare", "VA Benefits", "No Insurance", "Other", "Unsure"]} />
      <button onClick={() => Object.keys(filters).forEach((k) => setFilter(k, ""))}
        className="px-3 py-1.5 text-xs rounded-full bg-[#F9F8F6] border border-[#E5E5E0] text-[#334155] hover:bg-white"
        data-testid="filter-clear">Clear</button>
    </div>
  );
}

function FilterInput({ label, value, onChange, testId, type = "text" }) {
  return (
    <label className="flex flex-col text-[10px] uppercase tracking-wider text-[#1E3A8A]">
      {label}
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="mt-0.5 px-2 py-1 rounded-full border border-[#E5E5E0] bg-white text-xs text-[#0F172A] focus:outline-none focus:border-[#1E3A8A]" />
    </label>
  );
}
function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <label className="flex flex-col text-[10px] uppercase tracking-wider text-[#1E3A8A]">
      {label}
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="mt-0.5 px-2 py-1 rounded-full border border-[#E5E5E0] bg-white text-xs text-[#0F172A] focus:outline-none focus:border-[#1E3A8A]">
        <option value="">All</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

function Kpi({ label, value, icon: Icon, testId }) {
  return (
    <div className="bg-white border border-[#E5E5E0] rounded-2xl p-3" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-[#1E3A8A]">{label}</div>
        <Icon className="w-3.5 h-3.5 text-[#1E3A8A] opacity-60" />
      </div>
      <div className="font-display text-2xl font-semibold text-[#0F172A] mt-1">{value}</div>
    </div>
  );
}

function DistributionCard({ title, rows, testId, compact }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="bg-[#F9F8F6] border border-[#E5E5E0] rounded-xl p-3" data-testid={testId}>
      <div className="text-xs font-medium text-[#0F172A] mb-2 line-clamp-2">{title}</div>
      {rows.length === 0 ? (
        <div className="text-[11px] text-[#94A3B8]">No data yet.</div>
      ) : (
        <ul className={`space-y-1 ${compact ? "max-h-44 overflow-y-auto" : ""}`}>
          {rows.slice(0, compact ? 10 : 30).map((r) => (
            <li key={r.value} className="text-[11px] text-[#0F172A]">
              <div className="flex justify-between gap-2">
                <span className="truncate" title={r.value}>{r.value}</span>
                <span className="text-[#1E3A8A] font-medium">{r.count}</span>
              </div>
              <div className="h-1.5 rounded-full bg-[#E5E5E0] overflow-hidden">
                <div className="h-full bg-[#1E3A8A]" style={{ width: `${(r.count / max) * 100}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function OpenResponsesCard({ title, rows, testId }) {
  const [expanded, setExpanded] = useState(false);
  const cap = expanded ? rows.length : 5;
  return (
    <div className="bg-[#F9F8F6] border border-[#E5E5E0] rounded-xl p-3" data-testid={testId}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-xs font-medium text-[#0F172A] line-clamp-2">{title}</div>
        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#E0E7FF] text-[#1E3A8A] flex-shrink-0">
          {rows.length} {rows.length === 1 ? "response" : "responses"}
        </span>
      </div>
      {rows.length === 0 ? (
        <div className="text-[11px] text-[#94A3B8] italic">No responses yet for this question.</div>
      ) : (
        <>
          <ul className="space-y-2">
            {rows.slice(0, cap).map((r, i) => (
              <li
                key={`${r.submitted_at}-${i}`}
                className="text-[11px] text-[#0F172A] bg-white border border-[#E5E5E0] rounded-lg p-2"
                data-testid={`${testId}-row-${i}`}
              >
                <div className="leading-relaxed whitespace-pre-wrap">{r.answer}</div>
                <div className="text-[9px] uppercase tracking-wider text-[#1E3A8A] mt-1 flex flex-wrap gap-x-2">
                  <span>{r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : "—"}</span>
                  {r.county && <span>· {r.county}</span>}
                  {r.zip && <span>· {r.zip}</span>}
                </div>
              </li>
            ))}
          </ul>
          {rows.length > 5 && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              data-testid={`${testId}-toggle`}
              className="mt-2 text-[11px] text-[#1E3A8A] hover:underline"
            >
              {expanded ? "Show less" : `Show all ${rows.length}`}
            </button>
          )}
        </>
      )}
    </div>
  );
}


function SubmissionsTab({ definition, filters }) {
  const [list, setList] = useState(null);
  const openTextKeys = useMemo(() => {
    const keys = [];
    for (const s of (definition?.sections || [])) {
      for (const q of (s.questions || [])) {
        if (q.type === "long_text" || q.type === "short_text") keys.push(q.key);
      }
    }
    return keys;
  }, [definition]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/admin/assessments/${HCWI_SLUG}/submissions`, { params: { ...filters, limit: 100 } });
        setList(data);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [JSON.stringify(filters)]); // eslint-disable-line
  if (!list) return <div className="py-8 text-center text-[#1E3A8A]"><Loader2 className="w-5 h-5 animate-spin inline" /> Loading…</div>;
  return (
    <div className="bg-white border border-[#E5E5E0] rounded-2xl overflow-x-auto" data-testid="submissions-table">
      <table className="w-full text-sm">
        <thead className="bg-[#F9F8F6] border-b border-[#E5E5E0]">
          <tr>
            <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#1E3A8A]">Submitted</th>
            <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#1E3A8A]">County</th>
            <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#1E3A8A]">ZIP</th>
            <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#1E3A8A]">Open responses</th>
            <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-[#1E3A8A]">Time</th>
          </tr>
        </thead>
        <tbody>
          {list.submissions.length === 0 ? (
            <tr><td colSpan={5} className="px-3 py-8 text-center text-[#334155]">No submissions yet.</td></tr>
          ) : list.submissions.map((s) => (
            <tr key={s.id} className="border-t border-[#F0EFEB] align-top" data-testid={`submission-row-${s.id}`}>
              <td className="px-3 py-2 text-xs text-[#334155]">{new Date(s.submitted_at).toLocaleString()}</td>
              <td className="px-3 py-2 text-xs">{s.county || "—"}</td>
              <td className="px-3 py-2 text-xs">{s.zip || "—"}</td>
              <td className="px-3 py-2 text-xs text-[#334155]">
                {openTextKeys.map((k) => s.responses?.[k]).filter(Boolean).join(" · ") || "—"}
              </td>
              <td className="px-3 py-2 text-xs text-right">{s.completion_seconds ? `${(s.completion_seconds / 60).toFixed(1)}m` : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="px-3 py-2 text-[11px] text-[#1E3A8A] border-t border-[#F0EFEB]">
        Showing {list.submissions.length} of {list.total}
      </div>
    </div>
  );
}

function ContactsTab() {
  const [list, setList] = useState(null);
  const [giveawayOnly, setGiveawayOnly] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/admin/contact-leads`, { params: { limit: 100, giveaway_only: giveawayOnly || undefined } });
        setList(data);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [giveawayOnly]);
  const exportCsv = async () => {
    try {
      const token = getStoredToken();
      const params = new URLSearchParams();
      if (giveawayOnly) params.set("giveaway_only", "true");
      const resp = await fetch(`${API}/admin/contact-leads/export.csv?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("Export failed");
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "contact-leads.csv";
      a.click();
    } catch (e) { toast.error(e.message || "Export failed"); }
  };

  if (!list) return <div className="py-8 text-center text-[#1E3A8A]"><Loader2 className="w-5 h-5 animate-spin inline" /> Loading…</div>;
  return (
    <div className="space-y-3" data-testid="contacts-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <label className="inline-flex items-center gap-2 text-sm text-[#334155]">
          <input type="checkbox" checked={giveawayOnly} onChange={(e) => setGiveawayOnly(e.target.checked)}
            data-testid="filter-giveaway-only" />
          Giveaway entries only
        </label>
        <button onClick={exportCsv} data-testid="contacts-export-csv"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E5E5E0] text-[#1E3A8A] text-xs font-medium hover:bg-[#F9F8F6]">
          <FileText className="w-3.5 h-3.5" /> CSV
        </button>
      </div>
      <div className="bg-white border border-[#E5E5E0] rounded-2xl overflow-x-auto" data-testid="contacts-table">
        <table className="w-full text-sm">
          <thead className="bg-[#F9F8F6] border-b border-[#E5E5E0]">
            <tr>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#1E3A8A]">Submitted</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#1E3A8A]">Name</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#1E3A8A]">Email</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#1E3A8A]">Phone</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#1E3A8A]">ZIP</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#1E3A8A]">Consents</th>
            </tr>
          </thead>
          <tbody>
            {list.leads.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-[#334155]">No leads yet.</td></tr>
            ) : list.leads.map((l) => (
              <tr key={l.id} className="border-t border-[#F0EFEB]" data-testid={`lead-row-${l.id}`}>
                <td className="px-3 py-2 text-xs text-[#334155]">{new Date(l.created_at).toLocaleString()}</td>
                <td className="px-3 py-2 text-xs">{l.first_name} {l.last_name}</td>
                <td className="px-3 py-2 text-xs">{l.email}</td>
                <td className="px-3 py-2 text-xs">{l.phone || "—"}</td>
                <td className="px-3 py-2 text-xs">{l.zip || "—"}</td>
                <td className="px-3 py-2 text-[10px] space-x-1">
                  {l.contact_consent && <span className="px-1.5 py-0.5 rounded-full bg-[#E0E7FF] text-[#1E3A8A]">Contact</span>}
                  {l.marketing_consent && <span className="px-1.5 py-0.5 rounded-full bg-[#DCFCE7] text-[#166534]">Updates</span>}
                  {l.giveaway_entry && <span className="px-1.5 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E]">Giveaway</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="px-3 py-2 text-[11px] text-[#1E3A8A] border-t border-[#F0EFEB]">
          Showing {list.leads.length} of {list.total}
        </div>
      </div>
    </div>
  );
}
